import { useEffect, useMemo, useState } from "react";
import { Button, Card, Checkbox, Select, Stack, guardSubmit, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../lib/saveNotice";
import { api } from "../lib/api";
import {
  DATE_FORMAT_OPTIONS,
  NUMBER_FORMAT_OPTIONS,
  TIME_FORMAT_OPTIONS,
  asDateFormat,
  asNumberFormat,
  asTimeFormat,
  formatPreview,
  supportedTimeZones,
  type DateFormat,
  type NumberFormat,
  type TimeFormat,
} from "../lib/localeFormat";
import { FIELD_HINTS } from "../help/fieldHints";
import { useTheme } from "../shell/ThemeProvider";
import { notifyLocaleChanged } from "../shell/LocaleProvider";
import { PasswordSettings } from "./PasswordSettings";
import { LoxleyConnectionsCard } from "../loxley/LoxleyConnectionsCard";
import { useOptionalLoxley } from "../loxley/LoxleyProvider";

interface Preferences {
  display_theme: "organization" | "light" | "dark";
  timezone: string;
  currency: string;
  currency_symbol: string;
  use_org_locale: boolean;
  date_format?: string;
  time_format?: string;
  number_format?: string;
  saved: boolean;
  display_name?: string;
  email?: string;
  department?: string | null;
  reports_to_name?: string | null;
  currencies?: Array<{ code: string; symbol: string }>;
  org_timezone?: string;
  org_currency?: string;
  org_currency_symbol?: string;
  org_date_format?: string;
  org_time_format?: string;
  org_number_format?: string;
}

const FALLBACK_CURRENCIES = [
  { code: "AUD", symbol: "$" },
  { code: "USD", symbol: "$" },
  { code: "EUR", symbol: "€" },
  { code: "GBP", symbol: "£" },
  { code: "ZAR", symbol: "R" },
  { code: "NZD", symbol: "$" },
  { code: "CAD", symbol: "$" },
  { code: "JPY", symbol: "¥" },
  { code: "INR", symbol: "₹" },
  { code: "CHF", symbol: "CHF" },
  { code: "SGD", symbol: "$" },
];

export function AccountPage() {
  const { show } = useToast();
  const { setPreference } = useTheme();
  const loxley = useOptionalLoxley();
  const [value, setValue] = useState<Preferences | null>(null);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const zones = useMemo(() => supportedTimeZones(), []);
  const currencies = value?.currencies?.length ? value.currencies : FALLBACK_CURRENCIES;

  useEffect(() => {
    const controller = new AbortController();
    void api.get<Preferences>("/auth/preferences", controller.signal).then((row) => {
      setValue({ ...row, use_org_locale: row.use_org_locale !== false });
    }).catch(() => {
      if (!controller.signal.aborted) setMessage("User settings could not be loaded. Please reload to try again.");
    });
    return () => controller.abort();
  }, []);

  async function save() {
    if (!value) return;
    setSaving(true); setMessage("");
    try {
      const result = await api.put<Preferences>("/auth/preferences", {
        display_theme: value.display_theme,
        timezone: value.timezone,
        currency: value.currency,
        currency_symbol: value.currency_symbol,
        use_org_locale: value.use_org_locale,
        date_format: asDateFormat(value.date_format, value.currency),
        time_format: asTimeFormat(value.time_format, value.currency),
        number_format: asNumberFormat(value.number_format, value.currency),
      });
      setValue(result);
      setPreference(result.display_theme === "organization" ? "system" : result.display_theme);
      notifyLocaleChanged();
      setMessage("User settings saved.");
      reportSaved(show);
    } catch (err) { setMessage(reportSaveFailure(show, err, "User settings could not be saved. Please try again.")); }
    finally { setSaving(false); }
  }

  return (
    <Stack gap={4}>
      <h1>User settings</h1>
      <Card>
        <Stack gap={3}>
          <h2 className="home-section-title">Profile</h2>
          <p><strong>{value?.display_name || "Your name"}</strong></p>
          <p className="home-subtitle">{value?.email || "Loading email…"}</p>
          <p className="home-subtitle">Department: {value?.department || "—"}</p>
          <p className="home-subtitle">Reports to: {value?.reports_to_name || "None"}</p>
        </Stack>
      </Card>
      <Card>
        {value ? (
          <form onSubmit={guardSubmit((event) => { event.preventDefault(); return save(); })}>
            <Stack gap={4}>
              <label>Appearance
                <Select value={value.display_theme} onChange={(event) => setValue({ ...value, display_theme: event.target.value as Preferences["display_theme"] })}>
                  <option value="organization">Follow device</option>
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </Select>
              </label>
              <Checkbox
                label="Use organization timezone, currency, and formats"
                checked={value.use_org_locale}
                onChange={(event) => {
                  const follow = event.target.checked;
                  const currency = follow ? (value.org_currency || value.currency) : value.currency;
                  setValue({
                    ...value,
                    use_org_locale: follow,
                    timezone: follow ? (value.org_timezone || value.timezone) : value.timezone,
                    currency,
                    currency_symbol: follow ? (value.org_currency_symbol || value.currency_symbol) : value.currency_symbol,
                    date_format: follow ? asDateFormat(value.org_date_format, currency) : asDateFormat(value.date_format, currency),
                    time_format: follow ? asTimeFormat(value.org_time_format, currency) : asTimeFormat(value.time_format, currency),
                    number_format: follow
                      ? asNumberFormat(value.org_number_format, currency)
                      : asNumberFormat(value.number_format, currency),
                  });
                }}
              />
              <p className="home-subtitle">
                Organization default: {value.org_timezone || "UTC"} · {value.org_currency || "AUD"} · {formatPreview({
                  dateFormat: asDateFormat(value.org_date_format, value.org_currency),
                  timeFormat: asTimeFormat(value.org_time_format, value.org_currency),
                  numberFormat: asNumberFormat(value.org_number_format, value.org_currency),
                })}.
                Uncheck to choose how dates and numbers look on your screens. Stored records stay the same.
              </p>
              <label>
                Timezone
                <Select
                  value={value.timezone}
                  disabled={value.use_org_locale}
                  onChange={(event) => setValue({ ...value, timezone: event.target.value, use_org_locale: false })}
                >
                  {!zones.includes(value.timezone) && <option value={value.timezone}>{value.timezone}</option>}
                  {zones.map((zone) => (
                    <option key={zone} value={zone}>{zone}</option>
                  ))}
                </Select>
              </label>
              <label>Currency
                <Select
                  value={value.currency}
                  disabled={value.use_org_locale}
                  onChange={(event) => {
                    const code = event.target.value;
                    const symbol = currencies.find((item) => item.code === code)?.symbol ?? value.currency_symbol;
                    setValue({ ...value, currency: code, currency_symbol: symbol, use_org_locale: false });
                  }}
                >
                  {currencies.map((item) => (
                    <option key={item.code} value={item.code}>{item.code} ({item.symbol})</option>
                  ))}
                </Select>
              </label>
              <label>Currency symbol
                <Select
                  value={value.currency_symbol}
                  disabled={value.use_org_locale}
                  onChange={(event) => setValue({ ...value, currency_symbol: event.target.value, use_org_locale: false })}
                >
                  {Array.from(new Set(currencies.map((item) => item.symbol))).map((symbol) => (
                    <option key={symbol} value={symbol}>{symbol}</option>
                  ))}
                </Select>
              </label>
              <Select
                label="Date format"
                tooltip={FIELD_HINTS.org_date_format}
                value={asDateFormat(value.date_format, value.currency)}
                disabled={value.use_org_locale}
                onChange={(event) => setValue({
                  ...value,
                  date_format: event.target.value as DateFormat,
                  use_org_locale: false,
                })}
              >
                {DATE_FORMAT_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Select>
              <Select
                label="Time format"
                tooltip={FIELD_HINTS.org_time_format}
                value={asTimeFormat(value.time_format, value.currency)}
                disabled={value.use_org_locale}
                onChange={(event) => setValue({
                  ...value,
                  time_format: event.target.value as TimeFormat,
                  use_org_locale: false,
                })}
              >
                {TIME_FORMAT_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Select>
              <Select
                label="Number format"
                tooltip={FIELD_HINTS.org_number_format}
                value={asNumberFormat(value.number_format, value.currency)}
                disabled={value.use_org_locale}
                onChange={(event) => setValue({
                  ...value,
                  number_format: event.target.value as NumberFormat,
                  use_org_locale: false,
                })}
              >
                {NUMBER_FORMAT_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>{item.label}</option>
                ))}
              </Select>
              <p className="home-subtitle">
                Example: {formatPreview({
                  dateFormat: asDateFormat(value.date_format, value.currency),
                  timeFormat: asTimeFormat(value.time_format, value.currency),
                  numberFormat: asNumberFormat(value.number_format, value.currency),
                })}
              </p>
              <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save settings"}</Button>
            </Stack>
          </form>
        ) : !message && <p>Loading user settings…</p>}
        {message && <p role="status">{message}</p>}
      </Card>
      <PasswordSettings />
      {loxley?.available && loxley.orgId ? <LoxleyConnectionsCard orgId={loxley.orgId} /> : null}
    </Stack>
  );
}
